import { debounce } from 'lodash-es';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import LocalStorageUtil from '../Utils/LocalStorageUtil';

// React 19 types: `useRef<T>(null)` yields `RefObject<T | null>`, so accept a nullable-current
// ref here (the body already handles a null `current`).
export default function useRememberScrollTop<T extends HTMLElement>(
	elRef: T | RefObject<T | null> | null,
	hash: string,
) {
	const localStorageKey = `scrollTop.${hash}`;

	const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const existingScrollTop = LocalStorageUtil.getItem(localStorageKey);

		const el = elRef ? ('current' in elRef ? elRef.current : elRef) : null;

		const onScroll = debounce(() => {
			if (intervalRef.current) {
				clearInterval(Number(intervalRef.current));
			}
			const scrollTop = el?.scrollTop;
			LocalStorageUtil.setItem(localStorageKey, scrollTop?.toString() ?? '0');
		}, 50);

		if (el) {
			el.addEventListener('scroll', onScroll);
		}

		if (el && el.isConnected && existingScrollTop) {
			const scrollTop = parseInt(existingScrollTop);
			// Even though the element is connected, its contents may not be rendered, and therefore it may not have a height
			intervalRef.current = setInterval(() => {
				if (el.clientHeight > 0) {
					el.scrollTop = scrollTop;
					if (scrollTop === el.scrollTop && intervalRef.current) {
						clearInterval(Number(intervalRef.current));
					}
				}
			}, 100);
		}

		return () => {
			if (el) {
				el.removeEventListener('scroll', onScroll);
			}
		};
	}, [elRef, localStorageKey]);
}
